import type { UpdaterAnswer, UpdaterClusterMember, UpdaterEvent, UpdaterFileDependency, UpdaterRequest, UpdaterServer, UpdaterState } from '../types/modpackUpdater';
import { updaterScheduleRecovery } from './provisioningScheduleRecovery';

export const UPDATER_STEPS = ['Select', 'Prepare', 'Review', 'Maintenance', 'Stop', 'Patch', 'Start', 'Accept'];
export const UPDATER_FILE_LIMIT = 90 * 1024 * 1024;
export const MAX_REVIEW_CHUNKS = 1024;
const RECENT_EVENT_LIMIT = 1000;
export const updaterActive = (state: UpdaterState) => ['queued', 'running', 'waiting'].includes(state);
export const updaterStateLabel = (state: UpdaterState) => ({
  queued: 'Waiting for machine', running: 'In progress', waiting: 'Your review is needed', succeeded: 'Completed',
  failed: 'Stopped with an error', cancelled: 'Cancelled', needs_recovery: 'Recovery needed',
})[state] ?? state;

export function validUpdaterAnswer(request: UpdaterRequest, answer: UpdaterAnswer): boolean {
  if (request.kind === 'schedule') return answer === 'schedule:refresh'
    || (updaterScheduleRecovery(request)?.options.some(option => answer === `schedule:${option.id}`) ?? false);
  if (request.kind === 'confirm') return request.phrase ? answer === request.phrase : answer === true;
  if (request.kind === 'review') return answer === true;
  if (request.kind === 'prompt' || request.kind === 'secret') return typeof answer === 'string';
  const validIndex = (value: unknown) => typeof value === 'number' && Number.isInteger(value)
    && value >= 0 && value < (request.options?.length ?? 0);
  if (request.kind === 'choice') return validIndex(answer);
  if (request.kind === 'multichoice') return Array.isArray(answer) && answer.every(validIndex) && new Set(answer).size === answer.length;
  return false;
}

/** A provider file is pinned by the request; never turn an arbitrary URL into a download link. */
export function updaterFileDependency(request: UpdaterRequest): UpdaterFileDependency | null {
  const item = request.dependency;
  const datapack = item?.kind === 'datapack' && item.class_id === 6945;
  if (request.kind !== 'file' || !item || item.provider !== 'curseforge'
    || (item.kind !== undefined || item.class_id !== undefined) && !datapack
    || !Number.isSafeInteger(item.project_id) || item.project_id <= 0
    || !Number.isSafeInteger(item.file_id) || item.file_id <= 0
    || !Number.isSafeInteger(item.size) || item.size <= 0 || item.size > 1024 * 1024 * 1024
    || typeof item.filename !== 'string' || !(datapack ? /\.zip$/i : /\.jar$/i).test(item.filename)
    || /[\/\\\x00-\x1f\x7f]/.test(item.filename) || item.filename.length < 5 || item.filename.length > 255
    || !item.hashes || typeof item.hashes !== 'object') return null;
  const hashes = Object.entries(item.hashes);
  if ((!item.hashes.sha1 && !item.hashes.sha512) || hashes.some(([kind, hash]) => !['sha1', 'sha512', 'md5'].includes(kind)
    || typeof hash !== 'string' || !new RegExp(`^[a-f0-9]{${kind === 'sha1' ? 40 : kind === 'sha512' ? 128 : 32}}$`, 'i').test(hash))) return null;
  try {
    if (typeof item.official_url !== 'string' || item.official_url.length > 1000) return null;
    const url = new URL(item.official_url);
    const path = new RegExp(`^/minecraft/${datapack ? 'data-packs' : 'mc-mods'}/[a-z0-9-]+/files/([1-9]\\d*)$`).exec(url.pathname);
    if (url.protocol !== 'https:' || !['www.curseforge.com', 'curseforge.com'].includes(url.hostname)
      || url.username || url.password || url.port || url.search || url.hash || !path
      || path[1] !== String(item.file_id)) return null;
  } catch { return null; }
  return item;
}

/** Use received event sequence, never job.last_sequence: the event endpoint may page results. */
export function mergeUpdaterEvents(previous: UpdaterEvent[], incoming: UpdaterEvent[]): UpdaterEvent[] {
  if (!incoming.length) return previous;
  const bySequence = new Map(previous.map(event => [event.sequence, event]));
  for (const event of incoming) bySequence.set(event.sequence, event);
  const ordered = [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
  // Review evidence is durable for the loaded job, independently of noisy progress.
  // Evicting one early chunk would make a complete pending approval impossible.
  const durable = (event: UpdaterEvent) => event.type === 'request' || event.type === 'result'
    || event.type === 'log' && (typeof event.payload.review_id === 'string' || !!event.payload.deepclean_plan);
  let recent = 0;
  const kept: UpdaterEvent[] = [];
  for (let index = ordered.length - 1; index >= 0; index--) {
    const event = ordered[index];
    if (durable(event) || ++recent <= RECENT_EVENT_LIMIT) kept.push(event);
  }
  return kept.reverse();
}

/** An approval is usable only once its complete, immutable review has arrived. */
export function updaterReview(request: UpdaterRequest, events: UpdaterEvent[]) {
  const expected = request.review_chunks;
  if (request.review_id === undefined && expected === undefined) {
    return { lines: request.lines, complete: true, received: 0, expected: 0, error: null };
  }
  const incomplete = (received: number, error: string | null = null) => ({
    lines: [] as string[], complete: false, received, expected: expected ?? 0, error,
  });
  if (!request.review_id || !Number.isInteger(expected) || expected! < 1 || expected! > MAX_REVIEW_CHUNKS) {
    return incomplete(0, 'The review reference is invalid. Refresh this run before approving.');
  }
  const chunks = events.filter(event => event.type === 'log' && event.payload.review_id === request.review_id);
  const ordered = new Map<number, string[]>();
  let title: unknown;
  for (const { payload } of chunks) {
    const index = payload.review_index;
    if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= expected!
      || payload.review_chunks !== expected || typeof payload.review_title !== 'string'
      || (title !== undefined && title !== payload.review_title) || ordered.has(index)
      || !Array.isArray(payload.lines) || payload.lines.some(line => typeof line !== 'string')) {
      return incomplete(ordered.size, 'The complete review could not be verified. Refresh this run before approving.');
    }
    title = payload.review_title;
    ordered.set(index, payload.lines);
  }
  if (ordered.size !== expected) return incomplete(ordered.size);
  return { lines: Array.from({ length: expected }, (_, index) => ordered.get(index)!).flat(),
    complete: true, received: expected, expected, error: null };
}

/** Cache flattened immutable chunks by identity, not by transient progress or poll objects. */
export function createUpdaterReviewReader() {
  const cache = new Map<string, { expected: number | undefined; chunks: UpdaterEvent[]; review: ReturnType<typeof updaterReview> }>();
  return (request: UpdaterRequest, events: UpdaterEvent[]) => {
    if (!request.review_id) return updaterReview(request, events);
    const key = `${request.id}\0${request.review_id}`;
    const chunks = events.filter(event => event.type === 'log' && event.payload.review_id === request.review_id);
    const previous = cache.get(key);
    if (previous && previous.expected === request.review_chunks && previous.chunks.length === chunks.length
      && chunks.every((chunk, index) => chunk === previous.chunks[index])) return previous.review;
    const review = updaterReview(request, chunks);
    cache.set(key, { expected: request.review_chunks, chunks, review });
    return review;
  };
}

export function updaterSource(kind: string, value: string, version: string): string {
  if (kind === 'auto') return 'auto';
  const source = value.trim();
  if (!source) throw new Error('Enter the server pack location or provider project.');
  if (source.length > 4096 || /[\x00-\x1f\x7f]/.test(source)) throw new Error('Use a single source without control characters, up to 4096 characters.');
  if (kind === 'https') {
    let url: URL;
    try { url = new URL(source); } catch { throw new Error('Enter a complete HTTPS download URL.'); }
    if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Use HTTPS without a username or password in the URL.');
    return source;
  }
  if (kind === 'path') {
    if (!source.startsWith('/') || source.includes('//') || source.split('/').some(part => part === '..' || part === '.')) throw new Error('Enter a normalized absolute path on the server machine without . or .. segments.');
    return source;
  }
  if (!['curseforge', 'ftb', 'modrinth'].includes(kind)) throw new Error('Choose a supported pack source.');
  if (!/^[\w-]+$/.test(source)) throw new Error('Enter a provider project ID or slug.');
  if (!version.trim()) throw new Error('Enter the exact provider release or file ID to prepare.');
  if (/[@\x00-\x1f\x7f]/.test(version) || version.length > 200) throw new Error('Enter one release or file ID, up to 200 characters.');
  return `${kind}:${source}@${version.trim()}`;
}

/** An installed version is a comparison baseline, never an implicit update target. */
export function updaterReleaseTargets(server: UpdaterServer | null) {
  const current = server?.current_version?.trim();
  if (!current || server?.release_options?.advisor_error || !Array.isArray(server?.release_options?.targets)) return [];
  const seen = new Set<string>();
  return server.release_options.targets.filter(target => {
    if (typeof target.source !== 'string' || target.source.length > 4096
      || (target.source !== 'auto' && !/^(?:curseforge|ftb|modrinth):[^@\x00-\x1f\x7f]+@[^@\x00-\x1f\x7f]+$/.test(target.source))
      || typeof target.version !== 'string') return false;
    const version = target.version.trim();
    if (!version || version.length > 200 || /[\x00-\x1f\x7f]/.test(version)
      || sameUpdaterVersion(version, current) || seen.has(version)) return false;
    seen.add(version);
    return true;
  }).map(target => ({ ...target, version: target.version.trim() }));
}

export const sameUpdaterVersion = (left: string | null | undefined, right: string | null | undefined) =>
  !!left && !!right && left.trim().toLowerCase().replace(/^v(?=\d)/, '') === right.trim().toLowerCase().replace(/^v(?=\d)/, '');

/** Pin every selected member's current value; the server revalidates atomically. */
export function updaterBatchMembers(members: UpdaterClusterMember[], selected: string[], targetVersion: string) {
  if (!targetVersion.trim()) throw new Error('Choose a shared target pack version.');
  if (selected.length < 2 || new Set(selected).size !== selected.length) throw new Error('Select at least two different servers for a cluster update.');
  return selected.map(address => {
    const member = members.find(row => row.server_address === address);
    if (!member?.ready || !member.machine_id || !member.amp_name || !member.current_version) {
      throw new Error(`Refresh the cluster review: ${address} is not ready.`);
    }
    if (sameUpdaterVersion(member.current_version, targetVersion)) throw new Error(`${address} already has the selected version. Remove it from this update.`);
    return { server_address: address, expected_current_version: member.current_version };
  });
}

export function eventText(event: UpdaterEvent): string {
  const payload = event.payload;
  const lines = Array.isArray(payload.lines) ? payload.lines.map(String).filter(line => line.trim()) : [];
  if (event.type === 'request') return [typeof payload.title === 'string' ? payload.title : 'Operator review requested',
    ...lines].join('\n');
  const messages = ['message', 'detail', 'text', 'summary', 'title', 'error', 'context']
    .map(key => payload[key]).filter((value): value is string => typeof value === 'string' && !!value.trim());
  if (event.type === 'progress') {
    const { phase, done, total } = payload;
    const scalar = (value: unknown) => typeof value === 'string' ? value.trim() ? value : ''
      : typeof value === 'boolean' || typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
    const details = Array.isArray(payload.details) ? payload.details.map(scalar).filter(Boolean)
      : payload.details && typeof payload.details === 'object' ? Object.entries(payload.details)
        .flatMap(([key, value]) => scalar(value) ? [`${key.replace(/_/g, ' ')}: ${scalar(value)}`] : []) : [];
    const count = typeof done === 'number' && Number.isFinite(done) && done >= 0
      && typeof total === 'number' && Number.isFinite(total) && total > 0 && (total > 1 || done > 0)
      ? `${done} / ${total}` : '';
    return [...new Set([typeof phase === 'string' ? phase : '', ...lines, ...messages, ...details, count].filter(text => text.trim()))].join(' · ') || 'Progress reported.';
  }
  return [...new Set([...lines, ...messages])].join('\n') || JSON.stringify(payload);
}

/** Search only the displayed text. Searching must not reveal a hidden plan. */
export function machineOutputRows(events: UpdaterEvent[], showRaw = false, filter = '') {
  const query = filter.trim().toLowerCase();
  return events.flatMap(event => {
    if (!showRaw && event.type === 'log' && event.payload.review_id) return [];
    let text: string;
    if (event.type === 'plan') text = showRaw ? JSON.stringify(event.payload, null, 2) : 'Provisioning plan recorded.';
    else if (!showRaw && (event.payload.review_id || event.payload.deepclean_plan || event.type === 'request' && event.payload.kind === 'review')) {
      text = `${String(event.payload.title ?? event.payload.review_title ?? 'Recorded review')}${typeof event.payload.review_index === 'number' ? ` · section ${event.payload.review_index + 1}/${event.payload.review_chunks}` : ''}`;
    } else text = eventText(event);
    return [{ event, text }];
  }).filter(row => row.text && (!query || row.text.toLowerCase().includes(query)));
}

export function updaterProgress(events: UpdaterEvent[]): Record<string, unknown> {
  return events.filter(event => event.type === 'progress').reduce<Record<string, unknown>>((current, event) => {
    const payload = event.payload;
    const changedPhase = typeof payload.phase === 'string' && payload.phase !== current.phase;
    const changedTitle = typeof payload.title === 'string' && payload.title !== current.title;
    if (changedPhase || changedTitle) {
      current = { ...current };
      for (const key of ['phase', 'title', 'detail', 'done', 'total']) delete current[key];
    }
    return { ...current, ...payload };
  }, {});
}
